import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { motion, type Variants } from 'framer-motion';
import {
  ArrowRight, ShieldCheck, PlayCircle,
  Eye, Clock, Zap, LayoutList, RefreshCw, Share2,
  Activity, FileText, Layers,
  Gauge, ArrowDownWideNarrow,
  Send, Target, CheckCircle2, Building2, Sparkles, Database,
  Radio, BarChart2, Lightbulb, ScanLine,
} from 'lucide-react';
import {
  CAPABILITIES, FAQ, FLOW, MEASURES, PAIN_POINTS, PIPELINE, PRODUCT, SIGNALS,
} from './content';
import { DemoForm } from './DemoForm';

/**
 * Single-page marketing site for Intent360, reskinned to the KrillTech house
 * style. No router: every section is an anchor on one page, which keeps the
 * whole thing a single static asset on Cloudflare Pages and is what a buyer
 * scrolling on a phone actually wants.
 *
 * Motion is framer-motion. Content stays in content.ts; the icon and accent
 * colour for each item are attached here, in the presentation layer.
 */

/* ─── Motion helpers ─────────────────────────────────────────────────────────── */
const EASE = [0.22, 1, 0.36, 1] as const;

const reveal: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const staggerParent: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
};

const staggerItem: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
};

const inViewProps = {
  initial: 'hidden' as const,
  whileInView: 'show' as const,
  viewport: { once: true, amount: 0.2 },
};

/** Attach a CSS custom property to a style object without fighting the types. */
const withVar = (name: string, value: string, rest: CSSProperties = {}): CSSProperties =>
  ({ [name]: value, ...rest } as CSSProperties);

/* ─── Accent palette (Krill house colours) ───────────────────────────────────── */
const PALETTE = ['#2487ce', '#0dcfbe', '#f97316', '#8b5cf6', '#ec4899', '#22c55e'];
const color = (i: number) => PALETTE[i % PALETTE.length];

/* ─── Presentation maps (icons keyed to content order) ───────────────────────── */
const PAIN_ICONS = [Activity, ShieldCheck, Layers];
const MEASURE_ICONS = [Eye, Clock, ArrowDownWideNarrow, RefreshCw, Share2, Gauge];
const FLOW_ICONS = [Send, ScanLine, Gauge, Database];
const PIPELINE_ICONS = [Send, Activity, Target, CheckCircle2, Building2, Sparkles, Database];
const CAPABILITY_ICONS = [Send, ShieldCheck, Gauge, CheckCircle2, Building2, Sparkles, FileText, Database];

const DASHBOARD_METRICS = [
  { icon: Eye,        label: 'READS',  value: '2',       sub: 'Content views',   color: '#2487ce' },
  { icon: Clock,      label: 'TIME',   value: '11m 04s', sub: 'Active reading',  color: '#0dcfbe' },
  { icon: Zap,        label: 'EVENTS', value: '48',      sub: 'Tracked actions', color: '#f97316' },
  { icon: Clock,      label: 'AVG',    value: '5m 32s',  sub: 'Avg read time',   color: '#8b5cf6' },
  { icon: Eye,        label: 'PAGES',  value: '9/12',    sub: 'Pages viewed',    color: '#2487ce' },
  { icon: LayoutList, label: 'DEPTH',  value: '86%',     sub: 'Avg doc read',    color: '#0dcfbe' },
  { icon: RefreshCw,  label: 'RETURN', value: '2',       sub: 'Repeat visits',   color: '#f97316' },
  { icon: Share2,     label: 'SHARES', value: '1',       sub: 'Forwards',        color: '#8b5cf6' },
];

const FLOATING_CHIPS: Array<{ value: string; label: string; color: string; style: CSSProperties }> = [
  { value: '11m', label: 'Active read time', color: '#2487ce', style: { top: '-1.3rem', right: '-1.2rem' } },
  { value: '86%', label: 'Document depth',   color: '#0dcfbe', style: { bottom: '-1.3rem', right: '2.5rem' } },
];

/**
 * Outcome-oriented benefit cards for the dark "impact" section. Each is grounded
 * in a capability that already exists in content.ts — framing, not a new claim.
 */
const BENEFITS = [
  { icon: Target,      color: '#2487ce', title: 'Prioritise the right follow-ups', body: 'Rank prospects by how they actually read — reach out while intent is highest, not weeks after the download.' },
  { icon: BarChart2,   color: '#0dcfbe', title: 'End the lead-quality argument',   body: 'Every score decomposes into behaviour reps can see. No more debates between sales and marketing over which leads are real.' },
  { icon: Lightbulb,   color: '#f97316', title: 'Double down on what works',       body: 'See which assets earn genuine attention and which get skimmed, then invest your content budget accordingly.' },
  { icon: ShieldCheck, color: '#8b5cf6', title: 'Trust the number',                body: 'Bots and mail scanners are filtered before scoring, and measurement is first-party — so the signal survives a pipeline review.' },
];

/* ─── App ────────────────────────────────────────────────────────────────────── */
export function App() {
  return (
    <>
      <Nav />
      <main>
        <Hero />
        <PainPoints />
        <Measures />
        <Flow />
        <Pipeline />
        <Signals />
        <Capabilities />
        <Impact />
        <Faq />
        <Cta />
      </main>
      <Footer />
    </>
  );
}

/* ─── Nav ─────────────────────────────────────────────────────────────────────── */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav className={`nav${scrolled ? ' nav--scrolled' : ''}`}>
      <div className="wrap nav__inner">
        <a className="nav__mark" href="#top">
          <span className="nav__dot" aria-hidden="true" />
          {PRODUCT.name}<sup>™</sup>
        </a>
        <div className="nav__links">
          <a href="#how">How it works</a>
          <a href="#signals">Intent engine</a>
          <a href="#capabilities">Capabilities</a>
          <a href="#faq">FAQ</a>
          <a className="btn btn--primary" href="#demo">Request a demo <ArrowRight size={16} /></a>
        </div>
      </div>
    </nav>
  );
}

/* ─── Hero ────────────────────────────────────────────────────────────────────── */
function Hero() {
  const gridRef = useRef<HTMLDivElement>(null);

  // Subtle mouse-parallax on the grid, matching krilltech.com's hero.
  useEffect(() => {
    const el = gridRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const onMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 22;
      const y = (e.clientY / window.innerHeight - 0.5) * 22;
      el.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <header className="hero" id="top">
      <div ref={gridRef} className="hero__grid" aria-hidden="true" />
      <div className="hero__glow1" aria-hidden="true" />
      <div className="hero__glow2" aria-hidden="true" />

      <div className="wrap">
        <div className="hero__inner">
          {/* Copy */}
          <motion.div className="hero__copy" variants={staggerParent} initial="hidden" animate="show">
            <motion.span className="eyebrow" variants={staggerItem}>
              <span className="eyebrow__dot" aria-hidden="true" />
              {PRODUCT.tagline}
            </motion.span>

            <motion.h1 className="hero__headline" variants={staggerItem}>
              Find out who <span className="headline-accent">actually read it.</span>
            </motion.h1>

            <motion.p className="hero__sub" variants={staggerItem}>
              A download tells you a file moved. Intent360 tells you someone spent{' '}
              <strong>eleven minutes</strong> on your pricing page, came back twice, and forwarded
              it to their VP of Engineering. That is the difference between a lead list and a
              buying signal.
            </motion.p>

            <motion.div className="hero__ctas" variants={staggerItem}>
              <a className="btn btn--primary btn--lg" href="#demo">
                Request a demo <ArrowRight size={17} />
              </a>
              <a className="btn btn--onDark btn--lg" href="#how">
                <PlayCircle size={17} /> See how it works
              </a>
            </motion.div>

            <motion.p className="hero__note" variants={staggerItem}>
              <ShieldCheck size={14} /> {PRODUCT.heroNote}
            </motion.p>
          </motion.div>

          {/* Dashboard mockup */}
          <motion.div
            className="hero__visual"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
          >
            <div style={{ position: 'relative', width: '100%', maxWidth: 420 }}>
              <div className="dash" aria-hidden="true">
                <div className="dash__head">
                  <div>
                    <p className="dash__title">Engagement summary</p>
                    <p className="dash__sub">Lead reading behaviour · last 30 days</p>
                  </div>
                  <span className="live-chip"><span className="live-dot" />Live</span>
                </div>

                <div className="dash__grid">
                  {DASHBOARD_METRICS.map((m, i) => (
                    <motion.div
                      key={i}
                      className="dash__metric"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.45 + i * 0.06, duration: 0.4 }}
                    >
                      <div className="dash__metricTop">
                        <m.icon size={12} style={{ color: m.color }} />
                        <span className="dash__metricLabel">{m.label}</span>
                      </div>
                      <p className="dash__metricValue" style={{ color: m.color }}>{m.value}</p>
                      <p className="dash__metricSub">{m.sub}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="dash__score">
                  <span className="dash__scoreLabel">Buying-intent score</span>
                  <div className="dash__scoreTrack">
                    <motion.div
                      className="dash__scoreFill"
                      initial={{ width: 0 }}
                      animate={{ width: '82%' }}
                      transition={{ delay: 1, duration: 1.1, ease: EASE }}
                    />
                  </div>
                  <span className="dash__scoreValue">82</span>
                </div>
              </div>

              {/* Floating chips */}
              {FLOATING_CHIPS.map((c, i) => (
                <motion.div
                  key={c.label}
                  className="chip"
                  style={withVar('--chip-color', c.color, c.style)}
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
                >
                  <span className="chip__value">{c.value}</span>
                  <span className="chip__label">{c.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="wave" aria-hidden="true">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" fill="var(--bg)">
          <path d="M0,60 C360,10 1080,50 1440,15 L1440,60 Z" />
        </svg>
      </div>
    </header>
  );
}

/* ─── Section header ─────────────────────────────────────────────────────────── */
function Head({ kicker, title, lead, center, light }: {
  kicker: string; title: ReactNode; lead?: ReactNode; center?: boolean; light?: boolean;
}) {
  return (
    <motion.div
      className={`section__head${center ? ' section__head--center' : ''}`}
      variants={reveal}
      {...inViewProps}
    >
      <span className={`kicker${light ? ' kicker--light' : ''}`}>{kicker}</span>
      <h2 className="section__title">{title}</h2>
      {lead && <p className="section__lead">{lead}</p>}
    </motion.div>
  );
}

/* ─── Pain points / problem ──────────────────────────────────────────────────── */
function PainPoints() {
  return (
    <section className="section" id="why">
      <div className="wrap">
        <Head
          kicker="Why this exists"
          title={<>A download is not a <span className="gradient-text">signal.</span></>}
          lead="Every demand-gen team has a spreadsheet of people who filled in a form. Almost none of them can tell you which of those people actually read the thing."
        />
        <motion.div className="grid grid--3" variants={staggerParent} {...inViewProps}>
          {PAIN_POINTS.map((p, i) => {
            const Icon = PAIN_ICONS[i] ?? Activity;
            return (
              <motion.article className="card" key={p.title} variants={staggerItem}>
                <div className="icon-wrap" style={withVar('--m-color', color(i))}>
                  <Icon size={22} />
                </div>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Measures (6 signals) ───────────────────────────────────────────────────── */
function Measures() {
  return (
    <section className="section section--alt" id="measure">
      <div className="wrap">
        <Head
          center
          kicker="What gets measured"
          title={<>Six signals that reveal <span className="gradient-text">real buyer intent</span></>}
          lead="Each chosen because it survives contact with how people actually read — tabs left open, documents skimmed backwards, links forwarded to a colleague."
        />
        <motion.div className="grid grid--3" variants={staggerParent} {...inViewProps}>
          {MEASURES.map((m, i) => {
            const Icon = MEASURE_ICONS[i] ?? Gauge;
            return (
              <motion.article
                className="card"
                key={m.label}
                variants={staggerItem}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <div className="icon-wrap" style={withVar('--m-color', color(i))}>
                  <Icon size={22} />
                </div>
                <h3>{m.label}</h3>
                <p style={{ flex: 1 }}>{m.detail}</p>
                <CheckCircle2 className="card__check" size={16} />
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Flow (how it works) ────────────────────────────────────────────────────── */
function Flow() {
  return (
    <section className="section" id="how">
      <div className="wrap">
        <Head
          center
          kicker="How it works"
          title={<>Four steps, <span className="gradient-text">one database.</span></>}
          lead="Distribution, measurement, scoring, and delivery to your CRM happen in one system. Nothing is reconciled across tools after the fact, because nothing was split across tools to begin with."
        />
        <motion.div className="steps" variants={staggerParent} {...inViewProps}>
          {FLOW.map((s, i) => {
            const Icon = FLOW_ICONS[i] ?? Send;
            const c = color(i);
            return (
              <motion.article className="step" key={s.step} variants={staggerItem}>
                <span className="step__accent" style={{ background: c }} />
                <div className="icon-wrap" style={withVar('--m-color', c, { marginBottom: '0.75rem' })}>
                  <Icon size={20} />
                </div>
                <span className="step__num" style={{ color: c }}>{s.step}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Pipeline (intelligence chain) ──────────────────────────────────────────── */
function Pipeline() {
  return (
    <section className="section section--alt" id="pipeline">
      <div className="wrap">
        <Head
          kicker="The intelligence chain"
          title={<>From a sent campaign to a <span className="gradient-text">ranked lead.</span></>}
          lead="Each stage consumes the one above it. Reading behaviour becomes intent, intent is weighed against whether the contact is still reachable, and individual signals roll up to the account actually doing the buying."
        />
        <motion.div className="pipe" variants={staggerParent} {...inViewProps}>
          {PIPELINE.map((p, i) => {
            const Icon = PIPELINE_ICONS[i] ?? Activity;
            return (
              <motion.div className="pipe__row" key={p.stage} variants={staggerItem}>
                <span className="pipe__stage">
                  <span className="pipe__badge"><Icon size={16} /></span>
                  {p.stage}
                </span>
                <span className="pipe__note">{p.note}</span>
                <span className="pipe__arrow" aria-hidden="true">
                  {i < PIPELINE.length - 1
                    ? <ArrowRight size={16} style={{ transform: 'rotate(90deg)' }} />
                    : <Target size={16} />}
                </span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Signals (intent engine) ────────────────────────────────────────────────── */
function Signals() {
  return (
    <section className="section" id="signals">
      <div className="wrap">
        <Head
          kicker="Multi-source intent engine"
          title={<>One score, many signals, <span className="gradient-text">all decaying.</span></>}
          lead="Interest is perishable. Every signal contributes points that decay on their own half-life, saturate at a per-source cap so one noisy channel cannot dominate, and get a modest boost when activity clusters into a recent burst."
        />
        <motion.div className="tablewrap" variants={reveal} {...inViewProps}>
          <table>
            <thead>
              <tr>
                <th scope="col">Signal source</th>
                <th scope="col">Weight</th>
                <th scope="col">Half-life</th>
              </tr>
            </thead>
            <tbody>
              {SIGNALS.map((s) => (
                <tr key={s.source}>
                  <td><span className="source"><Radio size={15} />{s.source}</span></td>
                  <td><span className="weight-pill">{s.weight}</span></td>
                  <td className="num">{s.halfLife}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Capabilities ───────────────────────────────────────────────────────────── */
function Capabilities() {
  return (
    <section className="section section--alt" id="capabilities">
      <div className="wrap">
        <Head
          center
          kicker="Capabilities"
          title={<>What the <span className="gradient-text">platform does.</span></>}
          lead="From campaign execution to account-level intent, everything runs on one behavioural record — no reconciliation step, no black box."
        />
        <motion.div className="grid grid--2" variants={staggerParent} {...inViewProps}>
          {CAPABILITIES.map((c, i) => {
            const Icon = CAPABILITY_ICONS[i] ?? Sparkles;
            return (
              <motion.article className="card" key={c.title} variants={staggerItem}>
                <div className="cap-card__head">
                  <div className="icon-wrap" style={withVar('--m-color', color(i), { marginBottom: 0 })}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 style={{ marginBottom: '0.25rem' }}>{c.title}</h3>
                    <p className="cap-card__blurb">{c.blurb}</p>
                  </div>
                </div>
                <p>{c.detail}</p>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Impact (dark benefits) ─────────────────────────────────────────────────── */
function Impact() {
  return (
    <section className="section impact" id="impact">
      <div className="impact__bg" aria-hidden="true" />
      <div className="wrap" style={{ position: 'relative' }}>
        <Head
          center
          light
          kicker="The impact"
          title="What your team can do with Intent360"
          lead="The measurement is the means. This is what it changes on Monday morning."
        />
        <motion.div className="grid grid--4" variants={staggerParent} {...inViewProps}>
          {BENEFITS.map((b) => (
            <motion.article className="impact__card" key={b.title} variants={staggerItem}>
              <div className="impact__icon" style={withVar('--m-color', b.color)}>
                <b.icon size={24} />
              </div>
              <h3>{b.title}</h3>
              <p>{b.body}</p>
            </motion.article>
          ))}
        </motion.div>

        <motion.div className="strip" variants={reveal} {...inViewProps}>
          <div className="strip__left">
            <div className="strip__logo"><Gauge size={26} /></div>
            <div>
              <p className="strip__title">See it against your own content</p>
              <p className="strip__sub">Watch a document you already publish get read by someone real — and scored in front of you.</p>
            </div>
          </div>
          <div className="strip__actions">
            <a className="btn btn--white" href="#demo">Request a demo <ArrowRight size={16} /></a>
            <a className="btn btn--onDark" href="#how">How it works</a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── FAQ ────────────────────────────────────────────────────────────────────── */
function Faq() {
  return (
    <section className="section" id="faq">
      <div className="wrap">
        <Head
          kicker="Questions"
          title={<>The ones buyers <span className="gradient-text">actually ask.</span></>}
        />
        <motion.div className="faq" variants={staggerParent} {...inViewProps}>
          {FAQ.map((f) => (
            <motion.details key={f.q} variants={staggerItem}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </motion.details>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── CTA + demo ─────────────────────────────────────────────────────────────── */
function Cta() {
  const points = [
    'A live document read by a real person, scored in front of you',
    'First-party measurement — no third-party cookies, no cross-site tracking',
    'We use your details to contact you about Intent360. No newsletter, no list rental.',
  ];
  return (
    <section className="cta" id="demo">
      <div className="wrap">
        <div className="cta__inner">
          <motion.div variants={reveal} {...inViewProps}>
            <span className="kicker">Get started</span>
            <h2 className="section__title">
              See it against your <span className="gradient-text">own content.</span>
            </h2>
            <p className="section__lead">
              The fastest way to judge this is to watch a document you already publish get read by
              someone real. Tell us a bit and we will set that up.
            </p>
            <div className="cta__points">
              {points.map((pt) => (
                <div className="cta__point" key={pt}>
                  <CheckCircle2 size={18} /> <span>{pt}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="form-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <DemoForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─────────────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="footer">
      <div className="wrap footer__inner">
        <span className="footer__brand">
          <span className="nav__dot" aria-hidden="true" />
          {PRODUCT.name}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>
            — a product of <a href="https://krilltech.com" rel="noopener">{PRODUCT.parent}</a>
          </span>
        </span>
        <span><a href="#top">Back to top ↑</a></span>
      </div>
    </footer>
  );
}
